import { useEffect, useMemo, useState } from "react";
import { Check, Plus, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchProduct } from "@/lib/api";
import { useCart, formatNZD } from "@/lib/cart";
import { toast } from "sonner";

export default function FrequentlyBoughtTogether({ mainProduct }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [companions, setCompanions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Map of slug -> bool (mainProduct.slug is always implicitly true)
  const [selected, setSelected] = useState({});

  useEffect(() => {
    let active = true;
    const slugs = mainProduct?.frequently_bought_with || [];
    if (!slugs.length) {
      setCompanions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all(slugs.map((s) => fetchProduct(s).catch(() => null)))
      .then((results) => {
        if (!active) return;
        const got = results.filter(Boolean);
        setCompanions(got);
        const initial = {};
        got.forEach((p) => {
          initial[p.slug] = true;
        });
        setSelected(initial);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [mainProduct?.slug, mainProduct?.frequently_bought_with]);

  const toggle = (slug) => setSelected((s) => ({ ...s, [slug]: !s[slug] }));

  const chosenCompanions = useMemo(
    () => companions.filter((p) => selected[p.slug]),
    [companions, selected]
  );

  const bundleTotal = useMemo(() => {
    const base = Number(mainProduct?.price || 0);
    const extras = chosenCompanions.reduce(
      (sum, p) => sum + Number(p.price || 0),
      0
    );
    return base + extras;
  }, [mainProduct, chosenCompanions]);

  if (loading) return null;
  if (!companions.length) return null;

  const handleAddBundle = () => {
    // Add main product (no addons in bundle path — keep simple/clear)
    addItem(mainProduct, []);
    chosenCompanions.forEach((p) => addItem(p, []));
    const count = 1 + chosenCompanions.length;
    toast.success(
      `${count} item${count === 1 ? "" : "s"} added to cart`,
      { action: { label: "View cart", onClick: () => navigate("/cart") } }
    );
  };

  return (
    <section
      data-testid="fbt-section"
      className="mt-24 border-t border-[#E5E0D8] pt-12"
    >
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow mb-3">Take care of your piece</div>
          <h3 className="font-serif text-3xl md:text-4xl tracking-tight">
            Frequently bought together
          </h3>
        </div>
        <div className="hidden md:block text-sm text-[#6F4E37]">
          {chosenCompanions.length + 1} of {companions.length + 1} selected
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-8 items-stretch">
        {/* Visual stack of products with + signs */}
        <div className="md:col-span-8 flex flex-wrap items-center gap-3 sm:gap-4">
          {/* main product (always selected) */}
          <div className="flex flex-col items-center" data-testid={`fbt-main-${mainProduct.slug}`}>
            <div className="aspect-square w-24 sm:w-32 bg-[#F5F2EB] overflow-hidden border-2 border-[#2C2421]">
              <img src={mainProduct.image_url} alt={mainProduct.name} className="w-full h-full object-cover" />
            </div>
            <div className="mt-2 text-xs sm:text-sm font-medium text-center max-w-[8rem] truncate">
              {mainProduct.name}
            </div>
            <div className="text-xs text-[#6F4E37]">{mainProduct.price_label}</div>
          </div>

          {companions.map((p) => {
            const on = !!selected[p.slug];
            return (
              <div key={p.slug} className="flex items-center gap-3 sm:gap-4">
                <Plus size={18} className="text-[#A89F91] shrink-0" />
                <button
                  type="button"
                  data-testid={`fbt-toggle-${p.slug}`}
                  onClick={() => toggle(p.slug)}
                  aria-pressed={on}
                  className="flex flex-col items-center group"
                >
                  <div
                    className={`relative aspect-square w-24 sm:w-32 bg-[#F5F2EB] overflow-hidden border-2 transition-colors ${
                      on ? "border-[#2C2421]" : "border-[#E5E0D8] group-hover:border-[#A89F91]"
                    }`}
                  >
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className={`w-full h-full object-cover transition-opacity ${
                        on ? "opacity-100" : "opacity-50"
                      }`}
                    />
                    <span
                      className={`absolute top-1 right-1 h-5 w-5 inline-flex items-center justify-center text-xs ${
                        on
                          ? "bg-[#2C2421] text-[#FDFBF7]"
                          : "bg-[#FDFBF7]/80 text-transparent border border-[#A89F91]"
                      }`}
                      style={{ borderRadius: 2 }}
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                  </div>
                  <div className="mt-2 text-xs sm:text-sm font-medium text-center max-w-[8rem] truncate">
                    {p.name}
                  </div>
                  <div className="text-xs text-[#6F4E37]">{p.price_label}</div>
                </button>
              </div>
            );
          })}
        </div>

        {/* Summary card */}
        <div className="md:col-span-4">
          <div className="bg-[#F5F2EB] border border-[#E5E0D8] p-6 md:p-7 h-full flex flex-col">
            <div className="eyebrow mb-3">Bundle total</div>
            <div className="font-serif text-3xl md:text-4xl text-[#2C2421]" data-testid="fbt-total">
              {formatNZD(bundleTotal)}
            </div>
            <ul className="mt-4 space-y-1.5 text-sm text-[#2C2421]/85">
              <li className="flex items-center justify-between">
                <span className="truncate">{mainProduct.name}</span>
                <span className="text-[#6F4E37] tabular-nums">{formatNZD(mainProduct.price)}</span>
              </li>
              {chosenCompanions.map((p) => (
                <li key={p.slug} className="flex items-center justify-between">
                  <span className="truncate">{p.name}</span>
                  <span className="text-[#6F4E37] tabular-nums">{formatNZD(p.price)}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="fbt-add-bundle"
              onClick={handleAddBundle}
              className="mt-auto pt-6 inline-flex items-center justify-center gap-2 bg-[#2C2421] text-[#FDFBF7] py-3 text-sm font-medium hover:bg-[#4A3C38] transition-colors"
            >
              <ShoppingBag size={14} /> Add bundle to cart
            </button>
            <p className="mt-3 text-xs text-[#6F4E37] leading-relaxed text-center">
              Tap any companion to include or exclude it from your bundle.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
